import ApiService from './api';

/**
 * Chatbot Service - Handles all chatbot-related API calls
 */
class ChatbotService extends ApiService {
  constructor() {
    super();
  }
  /**
   * Load all chat sessions for the current user
   * @returns {Promise<Array>} Array of chat sessions
   */
  async getSessions() {
    let data;
    try {
      data = await this.get("/sessions");
    } catch (error) {
      if (error && error.status === 404) {
        data = await this.get("/chatbot/sessions");
      } else {
        throw error;
      }
    }
    return data.map(session => ({
      session_id: session.session_id,
      title: session.title,
      messages: []
    }));
  }

  /**
   * Load a specific session with all its messages
   * @param {string} sessionId - The session ID to load
   * @returns {Promise<Object>} Session data with messages
   */
  async getSessionMessages(sessionId) {
    try {
      return await this.get(`/sessions/${sessionId}`);
    } catch (error) {
      if (error && error.status === 404) {
        return await this.get(`/chatbot/sessions/${sessionId}`);
      }
      throw error;
    }
  }

  /**
   * Create a new chat session
   * @param {string} title - Title for the new session (default: "New Chat")
   * @returns {Promise<Object>} The created session
   */
  async createSession(title = "New Chat") {
    let data;
    try {
      data = await this.post("/sessions", { title });
    } catch (error) {
      if (error && error.status === 404) {
        data = await this.post("/chatbot/sessions", { title });
      } else {
        throw error;
      }
    }
    return {
      session_id: data.session_id,
      title: data.title,
      messages: []
    };
  }

  /**
   * Send a message and get an AI response
   * @param {string} sessionId - The session ID
   * @param {string} message - The message content
   * @returns {Promise<Object>} Object containing user_message and assistant_message
   */
  async sendMessage(sessionId, message) {
    return await this.post(`/chatbot/sessions/${sessionId}/chat`, { message });
  }



  /**
   * Update the title of a chat session
   * @param {string} sessionId - The session ID
   * @param {string} title - The new title
   * @returns {Promise<Object>} Response message
   */
  async updateSessionTitle(sessionId, title) {
    try {
      return await this.patch(`/sessions/${sessionId}/title?title=${encodeURIComponent(title)}`);
    } catch (error) {
      if (error && error.status === 404) {
        return await this.patch(`/chatbot/sessions/${sessionId}/title?title=${encodeURIComponent(title)}`);
      }
      throw error;
    }
  }

  /**
   * Delete a chat session
   * @param {string} sessionId - The session ID to delete
   * @returns {Promise<Object>} Response message
   */
  async deleteSession(sessionId) {
    try {
      return await this.delete(`/sessions/${sessionId}`);
    } catch (error) {
      if (error && error.status === 404) {
        return await this.delete(`/chatbot/sessions/${sessionId}`);
      }
      throw error;
    }
  }
}

// Export a singleton instance
export default new ChatbotService();
